import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import ToggleTabGroup from '../../components/atoms/ToggleTabGroup';
import { DEFAULT_SECTION_ACCESS_TYPE, SECTION_ACCESS_TYPES, VIEW_ONLY_SECTIONS } from '../../utils/consts';
import { translationPrefix as translationRootPrefix, CUSTOM_ACCESS_TYPE } from '../utils';
import SectionsPart from './SectionsPart';
import css from './SectionsStepCard.css';
import StepCard from './StepCard';

const translationPrefix = `${translationRootPrefix}.MANAGE_PERMISSIONS_PAGE`;

const SectionsStepCard = ({
    title,
    description,
    sections,
    sectionPermissions,
    onToggleAccessType,
    parts,
    translate,
}) => {
    const initialAccessType = useMemo(() => {
        const filteredSectionPermissions = sectionPermissions.filter(
            permission => !VIEW_ONLY_SECTIONS.includes(permission.name)
        );
        if (filteredSectionPermissions.every(permission => permission.access_type === SECTION_ACCESS_TYPES.EDIT)) {
            return SECTION_ACCESS_TYPES.EDIT;
        }
        if (filteredSectionPermissions.every(permission => permission.access_type === SECTION_ACCESS_TYPES.VIEW)) {
            return SECTION_ACCESS_TYPES.VIEW;
        }
        return CUSTOM_ACCESS_TYPE;
    }, [sectionPermissions]);

    const [selectedAccessType, setSelectedAccessType] = useState(initialAccessType);
    const accessTypesOptions = [...Object.values(SECTION_ACCESS_TYPES), CUSTOM_ACCESS_TYPE]
        .sort((a, b) => b - a)
        .map(option => ({
            label: translate(`${translationPrefix}.SECTION_ACCESS_TYPES_TAB.${option}`),
            option,
        }));

    const accessTypesRecord = useMemo(
        () =>
            sections.reduce((acc, section) => {
                const sectionPermission = sectionPermissions?.find(({ name }) => name === section.name);
                acc[section.name] = sectionPermission ? sectionPermission.access_type : DEFAULT_SECTION_ACCESS_TYPE;
                return acc;
            }, {}),
        [sections, sectionPermissions]
    );

    useEffect(() => {
        onToggleAccessType(VIEW_ONLY_SECTIONS, SECTION_ACCESS_TYPES.VIEW);
    }, [onToggleAccessType]);

    const handleOnToggle = newAccessType => {
        setSelectedAccessType(newAccessType);
        if (newAccessType !== CUSTOM_ACCESS_TYPE) {
            onToggleAccessType(
                sections.map(section => section.name).filter(sectionName => !VIEW_ONLY_SECTIONS.includes(sectionName)),
                newAccessType
            );
        }
    };

    return (
        <StepCard
            title={title}
            description={description}
            contentClassName={css.sectionsContainer}
            headerChildren={
                <ToggleTabGroup
                    options={accessTypesOptions}
                    selectedOption={selectedAccessType}
                    onToggle={handleOnToggle}
                />
            }
        >
            {parts.map(part => (
                <SectionsPart
                    key={`${part.sectionNames.join('-')}-sections-part`}
                    part={part}
                    accessTypesRecord={accessTypesRecord}
                    selectedAccessType={selectedAccessType}
                    onToggleAccessType={onToggleAccessType}
                />
            ))}
        </StepCard>
    );
};

SectionsStepCard.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    sectionPermissions: PropTypes.arrayOf(
        PropTypes.shape({
            access_type: PropTypes.number.isRequired,
            display_name: PropTypes.string.isRequired,
            feature_name: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            ui_key: PropTypes.string.isRequired,
        })
    ).isRequired,
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            display_name: PropTypes.string.isRequired,
            feature_name: PropTypes.string.isRequired,
        })
    ).isRequired,
    parts: PropTypes.arrayOf(
        PropTypes.shape({
            sectionNames: PropTypes.arrayOf(PropTypes.string).isRequired,
            displayName: PropTypes.string,
            tooltipImageSrc: PropTypes.string,
            tooltipDescriptionSectionName: PropTypes.string,
        })
    ).isRequired,
    onToggleAccessType: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SectionsStepCard);
